import { useEffect, useMemo, useRef, useState } from "react";

import { getLobKeyFromLobId } from "@/shared/redux/reducers/quoteAndAddonSlice";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import CompareBar from "./CompareBar";
import QuoteCard from "./QuoteCard";
import QuotesHeader from "./QuotesHeader";

type QuoteListItem = {
  status?: string;
  error?: unknown;
  isCompare?: boolean;
  insurer?: {
    name?: string;
  };
  plan?: {
    id?: string | number;
    name?: string;
    price?: {
      amount?: number | string | null;
      amount_with_vat?: number | string | null;
    };
  };
  sections?: Array<{
    title?: string;
    items?: Array<{
      label?: string;
      value?: unknown;
      amount?: unknown;
      currency?: unknown;
      [key: string]: unknown;
    }>;
  }>;
};

type QuoteListProps = {
  compact?: boolean;
  columns?: number;
  quotes?: QuoteListItem[];
  isLoading?: boolean;
  autoFetch?: boolean;
  LOB_ID?: number;
  shouldFetch?: boolean;
  fetchKey?: string;
  fetchQuotes?: () => Promise<void> | void;
  onBuyNow?: (quote: QuoteListItem) => void;
  showCompareBar?: boolean;
  disableBuyNow?: boolean;
  isReadOnly?: boolean;
  showDocumentUploadButton?: boolean;
  onDocumentUpload?: () => void;
  buyNowLoadingQuoteId?: string | number | null;
  showPdfButton?: boolean;
  onDownloadPdf?: (quote: QuoteListItem) => void;
};

const loadingThoughts = [
  'Basic "Trip Cancellation" only covers specific events like injury or weather.',
  "Standard medical care covers hospital stays, but Emergency Evacuation covers the cost of getting you to a proper hospital or back home.",
  "Most plans exclude pre-existing conditions by default.",
  "Free credit card insurance often has low limits and strict rules.",
];

const getQuoteCategory = (quote: {
  plan?: {
    name?: string;
  };
}) => {
  const planName = quote?.plan?.name?.toLowerCase() || "";
  if (planName.includes("third party")) {
    return "Third Party";
  }
  if (planName.includes("comprehensive")) {
    return "Comprehensive";
  }
  return "All";
};

const isRenderableQuote = (quote: QuoteListItem) => {
  if ("error" in quote && quote.error) return false;
  if (quote?.status) return false;
  if (!quote?.insurer?.name || !quote?.plan?.name) return false;

  const amount = Number(quote?.plan?.price?.amount_with_vat ?? quote?.plan?.price?.amount);
  return Number.isFinite(amount) && amount > 0;
};

const QuoteList = ({
  compact = false,
  columns = 1,
  quotes,
  isLoading = false,
  autoFetch = false,
  LOB_ID,

  shouldFetch = true,
  fetchKey = "default",
  fetchQuotes,
  onBuyNow,
  showCompareBar = true,
  disableBuyNow = false,
  isReadOnly = false,
  buyNowLoadingQuoteId = null,
  showDocumentUploadButton = false,
  onDocumentUpload,
  showPdfButton = false,
  onDownloadPdf,
}: QuoteListProps = {}) => {
  const {
    commonSlice: { quoteList: reduxQuotes = [], selectedAddOns = [] },
    quoteAndAddon,
  } = usePosterReducers();
  const lobKey = LOB_ID ? getLobKeyFromLobId(LOB_ID) : null;
  const cachedFetchKey = lobKey ? quoteAndAddon[lobKey].fetchKey : null;
  const hasCachedQuotes =
    Boolean(lobKey) &&
    cachedFetchKey === fetchKey &&
    quoteAndAddon[lobKey as keyof typeof quoteAndAddon].quoteList.length > 0;
  const [internalLoading, setInternalLoading] = useState(isLoading);
  const [fetchSettled, setFetchSettled] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  const lastFetchKeyRef = useRef<string | null>(null);

  const activeQuotes = quotes ?? reduxQuotes;

  const renderablePlans = useMemo(() => activeQuotes.filter(isRenderableQuote), [activeQuotes]);

  const availableTabs = useMemo(() => {
    const categories = new Set(
      renderablePlans.map(getQuoteCategory).filter((category) => category !== "All")
    );

    return categories.size > 0 ? ["All", ...Array.from(categories)] : ["All"];
  }, [renderablePlans]);

  const quoteCountByTab = useMemo(
    () =>
      availableTabs.reduce<Record<string, number>>((accumulator, tab) => {
        accumulator[tab] =
          tab === "All"
            ? renderablePlans.length
            : renderablePlans.filter((quote) => getQuoteCategory(quote) === tab).length;
        return accumulator;
      }, {}),
    [availableTabs, renderablePlans]
  );

  const filteredPlans = useMemo(
    () =>
      activeTab === "All"
        ? renderablePlans
        : renderablePlans.filter((quote) => getQuoteCategory(quote) === activeTab),
    [activeTab, renderablePlans]
  );

  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab("All");
    }
  }, [activeTab, availableTabs]);

  useEffect(() => {
    setInternalLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    let isMounted = true;

    if (!autoFetch || !fetchQuotes) {
      return;
    }

    if (!shouldFetch && hasCachedQuotes) {
      lastFetchKeyRef.current = fetchKey;
      setFetchSettled(true);
      setInternalLoading(false);
      return;
    }

    if (!shouldFetch) {
      return;
    }

    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }

    if (hasCachedQuotes) {
      lastFetchKeyRef.current = fetchKey;
      setFetchSettled(true);
      setInternalLoading(false);
      return;
    }

    lastFetchKeyRef.current = fetchKey;
    setFetchSettled(false);
    setFetchFailed(false);
    setInternalLoading(true);

    Promise.resolve(fetchQuotes())
      .catch((error) => {
        console.error("QuoteList fetchQuotes failed:", error);
        if (isMounted) {
          setFetchFailed(true);
        }
      })
      .finally(() => {
        if (isMounted) {
          setFetchSettled(true);
          setInternalLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [autoFetch, fetchKey, fetchQuotes, hasCachedQuotes, lobKey, reduxQuotes.length, shouldFetch]);

  const compareQuote = activeQuotes.filter((quote) => quote.isCompare);
  const hasActiveAddOnFilters = selectedAddOns.length > 0;
  const showNoQuotes =
    filteredPlans.length === 0 &&
    (!internalLoading || hasActiveAddOnFilters) &&
    (fetchSettled || fetchFailed || !autoFetch || hasActiveAddOnFilters);

  if (renderablePlans.length === 0 && internalLoading && !hasActiveAddOnFilters) {
    return (
      <div>
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-theme-text/40 backdrop-blur-xs" />
          <div className="relative h-52 w-full max-w-md rounded-2xl bg-theme-surface p-6 shadow-xl">
            <div className="mb-4 flex justify-center text-4xl">💡</div>
            <h2 className="text-center text-lg font-semibold text-theme-success-deep">
              Smart Insight
            </h2>
            <p className="mt-3 text-center text-sm font-semibold text-theme-text-muted">
              {loadingThoughts[0]}
            </p>
          </div>
        </div>

        {Array.from({ length: 6 }).map((_, index) => (
          <QuoteCard key={index} isLoading LOB_ID={LOB_ID} />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className={compact ? "space-y-2" : "space-y-4"}>
        <QuotesHeader
          compact={compact}
          totalQuotes={filteredPlans.length}
          tabs={availableTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          quoteCountByTab={quoteCountByTab}
          showDocumentUploadButton={showDocumentUploadButton}
          onDocumentUpload={onDocumentUpload}
        />
        {showNoQuotes ? (
          <div className="flex min-h-40 items-center justify-center rounded-lg border border-dashed border-theme-border-strong bg-theme-surface p-4 text-center">
            <div>
              <h3 className="text-sm font-semibold text-theme-text">No quote available</h3>
              <p className="mt-1 text-xs text-theme-text-soft">
                We could not find any matching quotes for the provided details.
              </p>
            </div>
          </div>
        ) : (
          <div
            className={
              columns === 2
                ? compact
                  ? "quote-list-grid grid grid-cols-1 gap-2 xl:grid-cols-2 mb-12"
                  : "grid grid-cols-1 gap-4 xl:grid-cols-2"
                : compact
                  ? "space-y-2"
                  : "space-y-4"
            }
          >
            {filteredPlans.map((quote, index) => (
              <QuoteCard
                key={`${quote?.insurer?.name || "quote"}-${quote?.plan?.name || index}-${index}`}
                data={quote}
                compact={compact}
                onBuyNow={onBuyNow}
                disableBuyNow={disableBuyNow}
                isReadOnly={isReadOnly}
                buyNowLoading={
                  buyNowLoadingQuoteId !== null &&
                  buyNowLoadingQuoteId !== undefined &&
                  String(quote?.plan?.id || "") === String(buyNowLoadingQuoteId)
                }
                showPdfButton={showPdfButton}
                onDownloadPdf={onDownloadPdf}
              />
            ))}
          </div>
        )}
      </div>

      {showCompareBar && compareQuote.length >= 2 ? (
        <CompareBar onBuyNow={onBuyNow} isReadOnly={isReadOnly} />
      ) : null}
    </>
  );
};

export default QuoteList;
